import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { SEOHead } from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';

/**
 * The status page behind a confirmation code.
 *
 * This is the `url` half of Meta's Data Deletion Request Callback contract:
 * the link plus the code must give the person a human-readable explanation of
 * where their request has got to, including the justification for anything
 * that is not deleted.
 *
 * Two rules shape the whole file:
 *
 * 1. A read failure is NEVER rendered as "no request with that code". Telling
 *    a person their deletion request was never recorded, when the row may be
 *    right there, is the worse of the two wrong answers. The two cases branch
 *    on the HTTP status, not on the absence of a body.
 * 2. The code is never written into a heading or a sentence. This is a page a
 *    person is likely to screenshot, and the code is a bearer token.
 *
 * The response body carries no personal data at all — see the Worker route.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.rooflyzer.pro/v1';
const SUPPORT_EMAIL = 'support@rooflyzer.pro';

type DeletionStatus = 'received' | 'in_progress' | 'completed';

type PageState =
  | { kind: 'loading' }
  | { kind: 'found'; status: DeletionStatus; requestedAt: string | null }
  | { kind: 'not_found' }
  | { kind: 'error' };

const STEPS: Array<{ key: DeletionStatus; label: string; glyph: string }> = [
  { key: 'received', label: 'Received', glyph: '✓' },
  { key: 'in_progress', label: 'In progress', glyph: '…' },
  { key: 'completed', label: 'Completed', glyph: '✓' },
];

const LINK_CLASS =
  'text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function isDeletionStatus(value: unknown): value is DeletionStatus {
  return value === 'received' || value === 'in_progress' || value === 'completed';
}

/**
 * `10 October 2026`, in a fixed locale and in UTC.
 *
 * `toLocaleDateString(undefined, …)` renders in the runtime's zone, which makes
 * any assertion on it machine-local.
 */
function formatRequestedAt(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The three-step chain, with one step marked.
 *
 * Each chip names its own state in words, so the chain is readable without
 * colour. `current` is index-based rather than status-based so the loading
 * state can render the chain with nothing marked.
 */
function StatusChain({ currentIndex }: { currentIndex: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-6">
      {STEPS.map((step, index) => {
        const done = currentIndex >= 0 && index < currentIndex;
        const current = index === currentIndex;
        const chip = done
          ? 'rounded-full bg-primary px-3 py-1 text-sm font-medium text-primary-foreground'
          : current
            ? 'rounded-full border border-primary/60 bg-primary/15 px-3 py-1 text-sm font-medium text-primary'
            : 'rounded-full border border-border bg-muted px-3 py-1 text-sm text-muted-foreground';
        return (
          <li key={step.key} className="flex items-center gap-x-2">
            <span className={chip} {...(current ? { 'aria-current': 'step' as const } : {})}>
              {step.label}
              {done || current ? <span aria-hidden="true"> {step.glyph}</span> : null}
            </span>
            {index < STEPS.length - 1 && (
              <span aria-hidden="true" className="text-muted-foreground">
                →
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function readHistoryStateCode(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.history.state as
      | { code?: unknown; usr?: { code?: unknown } | null }
      | null;
    if (typeof raw?.code === 'string' && raw.code) return raw.code;
    if (typeof raw?.usr?.code === 'string' && raw.usr.code) return raw.usr.code;
  } catch {
    // Ignore history access restrictions
  }
  return undefined;
}

export const DataDeletionStatusPage = () => {
  const { code: paramCode } = useParams<{ code: string }>();
  const location = useLocation();
  const [historyCode, setHistoryCode] = useState<string | undefined>(() =>
    readHistoryStateCode()
  );
  const locationStateCode = (location.state as { code?: string } | null)?.code;
  const stateCode = locationStateCode ?? readHistoryStateCode() ?? historyCode;
  const storedCode = (() => {
    try {
      return typeof sessionStorage !== 'undefined'
        ? (sessionStorage.getItem('rl_data_deletion_code') ?? undefined)
        : undefined;
    } catch {
      return undefined;
    }
  })();
  const code = paramCode ?? stateCode ?? storedCode;
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const activeControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setHistoryCode(readHistoryStateCode());
  }, [location]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handlePopState = () => {
      setHistoryCode(readHistoryStateCode());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!code || typeof window === 'undefined') return;
    try {
      sessionStorage.setItem('rl_data_deletion_code', code);
      const prevState =
        window.history.state && typeof window.history.state === 'object'
          ? (window.history.state as Record<string, unknown>)
          : {};
      const prevUsr =
        prevState.usr && typeof prevState.usr === 'object'
          ? (prevState.usr as Record<string, unknown>)
          : {};
      const nextState = {
        ...prevState,
        code,
        usr: { ...prevUsr, code },
      };
      if (/^\/data-deletion\/status\/[^/]+/i.test(window.location.pathname)) {
        window.history.replaceState(nextState, '', '/data-deletion/status');
      } else if (prevState.code !== code || prevUsr.code !== code) {
        window.history.replaceState(nextState, '');
      }
      setHistoryCode(code);
    } catch {
      // Ignore storage/history restrictions in constrained environments
    }
  }, [code]);

  const load = useCallback(async () => {
    activeControllerRef.current?.abort();
    if (!code) {
      activeControllerRef.current = null;
      setState({ kind: 'not_found' });
      return;
    }
    const controller = new AbortController();
    activeControllerRef.current = controller;
    const { signal } = controller;
    setState({ kind: 'loading' });
    try {
      const response = await fetch(
        `${API_BASE_URL}/webhooks/meta/data-deletion/status/${encodeURIComponent(code)}`,
        { signal, headers: { Accept: 'application/json' } }
      );
      if (signal.aborted) return;
      if (response.status === 404) {
        setState({ kind: 'not_found' });
        return;
      }
      if (!response.ok) {
        setState({ kind: 'error' });
        return;
      }
      const body: unknown = await response.json();
      if (signal.aborted) return;
      const status = (body as { status?: unknown } | null)?.status;
      if (!isDeletionStatus(status)) {
        // A value this page cannot render is an error, never a status. The
        // unknown value is not put on the screen.
        setState({ kind: 'error' });
        return;
      }
      const requestedAt = (body as { requested_at?: unknown }).requested_at;
      setState({
        kind: 'found',
        status,
        requestedAt: typeof requestedAt === 'string' ? requestedAt : null,
      });
    } catch (error) {
      if (signal.aborted || (error as { name?: string } | null)?.name === 'AbortError') return;
      setState({ kind: 'error' });
    }
  }, [code]);

  useEffect(() => {
    void load();
    return () => {
      activeControllerRef.current?.abort();
    };
  }, [load]);

  const currentIndex =
    state.kind === 'found' ? STEPS.findIndex((step) => step.key === state.status) : -1;
  const requestedOn = state.kind === 'found' ? formatRequestedAt(state.requestedAt) : null;

  return (
    <>
      <SEOHead
        title="Deletion request status"
        description="Check the status of a Rooflyzer data-deletion request with your confirmation code."
        canonicalUrl="/data-deletion"
        /* The code is in the path, so this page must never be indexed. */
        robots="noindex, nofollow"
      />

      <section className="pt-24 pb-24 px-4">
        <div className="max-w-xl mx-auto">
          <div
            /* `border-foreground/50`, not `border-border`. This card is the
               whole content of the page, so its outline is what identifies it
               as a surface. Measured against the page background:
               border-border is 1.33:1 in marketing-dark and 1.23:1 in light,
               both under the 3:1 boundary floor in WCAG 1.4.11.
               border-foreground/50 is the lowest neutral token step that
               clears it in both palettes — 5.39:1 dark, 3.77:1 light.
               border-muted-foreground/70 reaches 4.5:1 dark but only 2.72:1
               light, and border-primary/50 passes both while tinting the
               outline gold, which reads as a selected card. */
            className="rounded-lg border border-foreground/50 bg-card p-6 max-w-md mx-auto"
            aria-busy={state.kind === 'loading' || undefined}
          >
            {state.kind !== 'not_found' && state.kind !== 'error' && (
              <StatusChain currentIndex={currentIndex} />
            )}

            {state.kind === 'loading' && (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">Checking your request</h1>
                <p className="text-foreground/80">One moment.</p>
              </>
            )}

            {state.kind === 'found' && state.status === 'received' && (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">Request received</h1>
                <p className="text-foreground/80">
                  Rooflyzer received your deletion request
                  {requestedOn ? ` on ${requestedOn}` : ''}. The deletion has not started yet. Check
                  this page again in a few days.
                </p>
              </>
            )}

            {state.kind === 'found' && state.status === 'in_progress' && (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">Deletion in progress</h1>
                <p className="text-foreground/80">
                  Rooflyzer is deleting the data it holds about you. The deletion is complete within
                  30 days{requestedOn ? ` of ${requestedOn}` : ''}.
                </p>
              </>
            )}

            {state.kind === 'found' && state.status === 'completed' && (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">Deletion complete</h1>
                {/* Meta's required justification for anything retained. Do not
                    cut this sentence. */}
                <p className="text-foreground/80">
                  Rooflyzer deleted the data it holds about you. Some records stay, because United
                  States law requires a business to keep a record of a transaction, of a signed
                  document and of a telephone consent for a set period. Rooflyzer keeps those
                  records for that period and no longer.
                </p>
              </>
            )}

            {state.kind === 'not_found' && (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  No request with that code
                </h1>
                <p className="text-foreground/80">
                  Rooflyzer has no deletion request with the code you entered. Check the code and
                  try again. If the code is right, send an email to{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className={LINK_CLASS}>
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
                <p className="mt-4">
                  <Link
                    to="/data-deletion"
                    className={`${LINK_CLASS} min-h-11 inline-flex items-center`}
                  >
                    Back to data deletion
                  </Link>
                </p>
              </>
            )}

            {state.kind === 'error' && (
              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  The status is not available
                </h1>
                <p className="text-foreground/80">
                  Rooflyzer cannot read the status of your request now. This is a problem on our
                  side, not with your code. Try again in a few minutes.
                </p>
                <Button type="button" className="min-h-11 mt-4" onClick={() => void load()}>
                  Try again
                </Button>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};
